import React from "react";
import PropTypes from "prop-types";
import { Box, Card, Grid, Skeleton, Stack } from "@mui/material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

export const NewsHeroSkeleton = () => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 4,
      }}
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Skeleton variant="rounded" width={160} height={26} sx={{ borderRadius: borderRadiusPx.pill }} />
            <Skeleton variant="text" width="80%" height={48} />
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="60%" height={24} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: borderRadiusPx.lg }} />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: borderRadiusPx.lg }} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export const NewsFeaturedSkeleton = () => {
  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        mb: 4,
      }}
    >
      <Grid container>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rectangular" height={320} width="100%" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ p: { xs: 3, sm: 4 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: borderRadiusPx.pill, mb: 2 }} />
            <Skeleton variant="text" width="90%" height={36} sx={{ mb: 1.5 }} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="95%" height={20} />
            <Skeleton variant="text" width="70%" height={20} sx={{ mb: 3 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="rounded" width={140} height={38} sx={{ borderRadius: borderRadiusPx.md }} />
              <Skeleton variant="circular" width={38} height={38} />
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export const NewsCardSkeleton = ({ viewMode = "grid" }) => {
  if (viewMode === "list") {
    return (
      <Card
        sx={{
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          p: 2.5,
          mb: 2.5,
        }}
      >
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: borderRadiusPx.lg }} />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: borderRadiusPx.pill }} />
                <Skeleton variant="text" width={100} height={20} />
              </Stack>
              <Skeleton variant="text" width="85%" height={28} />
              <Skeleton variant="text" width="100%" height={18} />
              <Skeleton variant="text" width="60%" height={18} />
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                <Skeleton variant="text" width={90} height={18} />
                <Skeleton variant="rounded" width={110} height={32} sx={{ borderRadius: borderRadiusPx.md }} />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Skeleton variant="rectangular" height={190} width="100%" />
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: borderRadiusPx.pill }} />
          <Skeleton variant="text" width={90} height={20} />
        </Stack>
        <Skeleton variant="text" width="90%" height={26} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={16} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mb: 2 }} />
        <Box sx={{ mt: "auto", pt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Skeleton variant="text" width={80} height={18} />
          <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: borderRadiusPx.md }} />
        </Box>
      </Box>
    </Card>
  );
};

NewsCardSkeleton.propTypes = {
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

export const NewsGridSkeleton = ({ count = 6, viewMode = "grid" }) => {
  return (
    <Grid container spacing={3}>
      {Array.from({ length: count }).map((_, idx) => (
        <Grid item xs={12} sm={viewMode === "grid" ? 6 : 12} md={viewMode === "grid" ? 4 : 12} key={idx}>
          <NewsCardSkeleton viewMode={viewMode} />
        </Grid>
      ))}
    </Grid>
  );
};

NewsGridSkeleton.propTypes = {
  count: PropTypes.number,
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

const NewsSkeleton = {
  NewsHeroSkeleton,
  NewsFeaturedSkeleton,
  NewsCardSkeleton,
  NewsGridSkeleton,
};

export default NewsSkeleton;
